/* FormTree.js
 * Tree view of form structure with inline rename, drag handle, inline actions, and HTML5 DnD
 */

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import "./FormTree.css";

/* ---- Type icons ---- */
import NotesIcon from '@mui/icons-material/Notes';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import StorageIcon from '@mui/icons-material/Storage';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InterpreterModeIcon from '@mui/icons-material/InterpreterMode';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InfoIcon from '@mui/icons-material/Info';

/* ---- UI icons (handle/add/delete) ---- */
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const ICONS = {
  information: InfoIcon,
  submissionlist: FormatListBulletedIcon,
  select: StorageIcon,
  text: WysiwygIcon,
  checkbox: CheckBoxIcon,
  file: AttachFileIcon,
  authors: InterpreterModeIcon,
  affiliations: AssuredWorkloadIcon,
  radio: RadioButtonCheckedIcon,
  summary: SummarizeIcon,
};

function ControlIcon({ type }) {
  const key = String(type || "").toLowerCase();
  const Icon = ICONS[key] || WysiwygIcon;
  return <Icon fontSize="small" />;
}

/**
 * Props:
 *  - pages, dictionaryResolvers, lang, selection, formName, formToken
 *  - onSelectForm, onSelectPage, onSelectControl
 *  - onRenamePageTitle, onRenameControlKey
 *  - onAddControl?(pageIndex, afterControlIndex|null)
 *  - onDeletePage?(pageIndex)
 *  - onDeleteControl?(pageIndex, controlIndex)
 *  - onMovePage?(fromIndex, toIndex)                          // drop BEFORE target page
 *  - onMoveControl?(fromPage, fromControl, toPage, toControl) // drop BEFORE target control; page-row = append
 */
export default function FormTree({
  pages,
  formName,
  formToken,
  dictionaryResolvers,
  lang,
  selection,
  onSelectForm,
  onSelectPage,
  onSelectControl,
  onRenamePageTitle,
  onRenameControlKey,
  onAddControl,
  onDeletePage,
  onDeleteControl,
  onMovePage,
  onMoveControl,
}) {
  const [openPages, setOpenPages] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const inputRef = useRef(null);

  // --- DnD state ---
  const dragRef = useRef(null); // { kind:'page'|'control', pageIndex, controlIndex? }
  const [dragSource, setDragSource] = useState(null);
  const [drop, setDrop] = useState(null); // { kind:'page-before'|'control-before'|'page-append', pageIndex, controlIndex? }

  const cleanupDnD = useCallback(() => {
    dragRef.current = null;
    setDragSource(null);
    setDrop(null);
  }, []);

  // Use the whole row as the drag image so the *row* moves with the cursor
  const setDragImageFromRow = useCallback((e) => {
    const row = e.currentTarget.closest(".fe-tree__item");
    if (!row || !e.dataTransfer?.setDragImage) return;
    const rect = row.getBoundingClientRect();
    e.dataTransfer.setDragImage(row, 24, rect.height / 2);
  }, []);

  const editKey = useMemo(() => {
    if (!editing) return null;
    const { type, pageIndex, controlIndex } = editing;
    return `${type}:${pageIndex}:${controlIndex ?? ''}`;
  }, [editing?.type, editing?.pageIndex, editing?.controlIndex]);

  useEffect(() => {
    if (!editKey || !inputRef.current) return;
    inputRef.current.focus();
    const len = inputRef.current.value?.length ?? 0;
    inputRef.current.setSelectionRange?.(len, len);
  }, [editKey]);

  const togglePage = useCallback((pi) => {
    setOpenPages((prev) => {
      const next = new Set(prev);
      if (next.has(pi)) next.delete(pi);
      else next.add(pi);
      return next;
    });
  }, []);

  const pageList = useMemo(() => (Array.isArray(pages) ? pages : []), [pages]);

  // Presence check via resolver (no duplication of phrase maps)
  const getPhraseSync = dictionaryResolvers?.getPhraseSync;
  const phraseExists = useCallback((key) => {
    if (!key) return true; // nothing to resolve => not “missing”
    if (typeof getPhraseSync !== "function") return true; // no resolver: don't warn
    const v = getPhraseSync(lang, key);
    return typeof v === "string" && v.length > 0;
  }, [getPhraseSync, lang]);

  const isSelectedForm = selection?.kind === "form";
  const selectedPageIndex =
    selection?.kind === "page" || selection?.kind === "control"
      ? selection.pageIndex
      : -1;
  const selectedControlIndex =
    selection?.kind === "control" ? selection.controlIndex : -1;

  const commitEdit = useCallback(() => {
    if (!editing) return;
    const { type, pageIndex, controlIndex, value } = editing;
    if (type === "page") onRenamePageTitle?.(pageIndex, value);
    else if (type === "control") onRenameControlKey?.(pageIndex, controlIndex, value);
    setEditing(null);
  }, [editing, onRenamePageTitle, onRenameControlKey]);

  const cancelEdit = useCallback(() => setEditing(null), []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  }, [commitEdit, cancelEdit]);

  // ---------- DnD handlers ----------

  const onDragStartPage = useCallback((e, pi) => {
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", `page:${pi}`); } catch {}
      setDragImageFromRow(e);
    }
    const payload = { kind: "page", pageIndex: pi };
    dragRef.current = payload;
    setDragSource(payload);
  }, [setDragImageFromRow]);

  const onDragStartControl = useCallback((e, pi, ci) => {
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", `control:${pi}:${ci}`); } catch {}
      setDragImageFromRow(e);
    }
    const payload = { kind: "control", pageIndex: pi, controlIndex: ci };
    dragRef.current = payload;
    setDragSource(payload);
  }, [setDragImageFromRow]);

  const onDragEndAny = useCallback((e) => {
    e.stopPropagation();
    cleanupDnD();
  }, [cleanupDnD]);

  const onDragOverPageRow = useCallback((e, pi) => {
    const cur = dragRef.current;
    if (!cur) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    setDrop({ kind: cur.kind === "page" ? "page-before" : "page-append", pageIndex: pi });
  }, []);

  const onDropOnPageRow = useCallback((e, pi) => {
    const cur = dragRef.current;
    if (!cur) return;
    e.preventDefault();

    if (cur.kind === "page") {
      const from = cur.pageIndex;
      const to = pi;
      if (typeof onMovePage === "function" && from !== to) onMovePage(from, to);
    } else if (cur.kind === "control") {
      const fromP = cur.pageIndex;
      const fromC = cur.controlIndex;
      const toP = pi;
      const toC = (pages?.[toP]?.controls?.length ?? 0); // append
      if (typeof onMoveControl === "function") onMoveControl(fromP, fromC, toP, toC);
    }

    cleanupDnD();
  }, [onMovePage, onMoveControl, pages, cleanupDnD]);

  const onDragOverControlRow = useCallback((e, pi, ci) => {
    const cur = dragRef.current;
    if (!cur) return;
    if (cur.kind !== "control") return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    setDrop({ kind: "control-before", pageIndex: pi, controlIndex: ci });
  }, []);

  const onDropOnControlRow = useCallback((e, pi, ci) => {
    const cur = dragRef.current;
    if (!cur) return;
    e.preventDefault();

    if (cur.kind === "control") {
      const fromP = cur.pageIndex;
      const fromC = cur.controlIndex;
      const toP = pi;
      const toC = ci; // BEFORE this control
      if (typeof onMoveControl === "function") onMoveControl(fromP, fromC, toP, toC);
    }

    cleanupDnD();
  }, [onMoveControl, cleanupDnD]);

  return (
    <div className="fe-tree">
      {/* Form root (no drag handle) */}
      <button
        className={`fe-tree__item fe-tree__item--form ${isSelectedForm ? "is-active" : ""}`}
        onClick={onSelectForm}
        aria-label="Form"
        aria-current={isSelectedForm ? "true" : undefined}
      >
        <span className="fe-tree__icon" aria-hidden>📄</span>
        <span className="fe-tree__label">Form - {formName} - {formToken}</span>
      </button>

      {/* Pages */}
      <div className="fe-tree__section">Pages</div>
      <ul className="fe-tree__list">
        {pageList.map((p, pi) => {
          const isOpen = openPages.has(pi);
          const isActivePage = selectedPageIndex === pi && selection?.kind === "page";
          const controls = Array.isArray(p?.controls) ? p.controls : [];
          const pageTitle = p?.title || `Page ${pi + 1}`;
          const editingThisPage = editing?.type === "page" && editing.pageIndex === pi;

          const pageRowDropActive =
            drop &&
            (drop.kind === "page-before" || drop.kind === "page-append") &&
            drop.pageIndex === pi;

          const isDraggingThisPage =
            dragSource?.kind === "page" && dragSource.pageIndex === pi;

          return (
            <li key={`page-${pi}`} className="fe-tree__node">
              <div
                className={
                  `fe-tree__item fe-draggable ${isActivePage ? "is-active" : ""} ${isDraggingThisPage ? "fe-draggable--dragging" : ""} ${pageRowDropActive ? "fe-drop-target" : ""}`
                }
                aria-current={isActivePage ? "true" : undefined}
                onDragOver={(e) => onDragOverPageRow(e, pi)}
                onDrop={(e) => onDropOnPageRow(e, pi)}
              >
                {/* drag handle (pages) */}
                <span
                  className="fe-drag-handle"
                  draggable
                  onDragStart={(e) => onDragStartPage(e, pi)}
                  onDragEnd={onDragEndAny}
                  aria-label="Drag page"
                  title="Drag to reorder page"
                >
                  <DragIndicatorIcon fontSize="small" />
                </span>

                {/* disclosure */}
                <button
                  className="fe-tree__disclosure"
                  onClick={() => togglePage(pi)}
                  aria-label={isOpen ? "Collapse page" : "Expand page"}
                >
                  {isOpen ? "▾" : "▸"}
                </button>

                {/* title or input */}
                {editingThisPage ? (
                  <input
                    ref={inputRef}
                    className="fe-edit"
                    type="text"
                    value={editing.value}
                    onChange={(e) =>
                      setEditing((prev) => ({ ...prev, value: e.target.value }))
                    }
                    onBlur={commitEdit}
                    onKeyDown={handleKeyDown}
                    aria-label="Edit page title"
                  />
                ) : (
                  <button
                    className="fe-tree__button"
                    onClick={() => onSelectPage?.(pi)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditing({ type: "page", pageIndex: pi, value: pageTitle });
                    }}
                    title={`${pageTitle}\npage ${pi}\nDouble click to edit`}
                    aria-label={pageTitle}
                  >
                    <span className="fe-tree__icon" aria-hidden>📑</span>
                    <span className="fe-tree__label">{pageTitle}</span>
                  </button>
                )}

                {/* inline actions: only when this page is the current selection */}
                {isActivePage && (
                  <div className="fe-actions-inline fe-actions-inline--light">
                    <button
                      className="fe-icon-btn fe-icon-btn--add"
                      title="Add element"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddControl?.(
                          pi,
                          selection?.kind === "control" && selection.pageIndex === pi
                            ? selection.controlIndex
                            : null
                        );
                      }}
                    >
                      <LibraryAddIcon fontSize="small" />
                    </button>

                    <button
                      className="fe-icon-btn fe-icon-btn--delete"
                      title="Delete page"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePage?.(pi);
                      }}
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </button>
                  </div>
                )}
              </div>

              {/* Controls */}
              {isOpen && (
                <ul className="fe-tree__controls">
                  {controls.length === 0 ? (
                    <li className="fe-tree__empty">No elements</li>
                  ) : (
                    controls.map((c, ci) => {
                      const isActiveCtrl =
                        selectedPageIndex === pi && selectedControlIndex === ci;
                      const editingThisCtrl =
                        editing?.type === "control" &&
                        editing.pageIndex === pi &&
                        editing.controlIndex === ci;

                      // Missing marker via resolver presence checks
                      const hasLabel = phraseExists(c.label);
                      const hasContent = phraseExists(c.content);
                      const missing = !(hasLabel && hasContent);

                      const ctrlName = c?.key || `Control ${ci + 1}`;
                      const ctrlType = c?.type ? `\n${c.type}` : "";

                      const controlRowDropActive =
                        drop &&
                        drop.kind === "control-before" &&
                        drop.pageIndex === pi &&
                        drop.controlIndex === ci;

                      const isDraggingThisControl =
                        dragSource?.kind === "control" &&
                        dragSource.pageIndex === pi &&
                        dragSource.controlIndex === ci;

                      return (
                        <li key={`ctrl-${pi}-${ci}`}>
                          <div
                            className={
                              `fe-tree__item is-nested fe-draggable ${isActiveCtrl ? "is-active" : ""} ${isDraggingThisControl ? "fe-draggable--dragging" : ""} ${controlRowDropActive ? "fe-drop-target" : ""}`
                            }
                            onClick={() => onSelectControl?.(pi, ci)}
                            title={`${ctrlName}\npage ${pi}${ctrlType}\nDouble click to edit`}
                            aria-label={ctrlName}
                            aria-current={isActiveCtrl ? "true" : undefined}
                            onDragOver={(e) => onDragOverControlRow(e, pi, ci)}
                            onDrop={(e) => onDropOnControlRow(e, pi, ci)}
                          >
                            {/* drag handle (controls) */}
                            <span
                              className="fe-drag-handle-control"
                              draggable
                              onDragStart={(e) => onDragStartControl(e, pi, ci)}
                              onDragEnd={onDragEndAny}
                              aria-label="Drag element"
                              title="Drag to move element"
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </span>

                            {/* type icon */}
                            <span className="fe-tree__icon" aria-hidden>
                              <ControlIcon type={c?.type} />
                            </span>

                            {/* key (or input when renaming) */}
                            {editingThisCtrl ? (
                              <input
                                ref={inputRef}
                                className="fe-edit"
                                type="text"
                                value={editing.value}
                                onChange={(e) =>
                                  setEditing((prev) => ({ ...prev, value: e.target.value }))
                                }
                                onBlur={commitEdit}
                                onKeyDown={handleKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Edit control key"
                              />
                            ) : (
                              <span
                                className="fe-tree__label fe-tree__label--editable"
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setEditing({
                                    type: "control",
                                    pageIndex: pi,
                                    controlIndex: ci,
                                    value: c?.key || "",
                                  });
                                }}
                              >
                                {ctrlName}
                              </span>
                            )}

                            {missing ? (
                              <span className="fe-tree__warn" title="Missing dictionary text">•</span>
                            ) : null}

                            {/* delete control: only when this control is the current selection */}
                            {isActiveCtrl && (
                              <div className="fe-actions-inline fe-actions-inline--light">
                                <button
                                  className="fe-icon-btn fe-icon-btn--delete"
                                  title="Delete element"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteControl?.(pi, ci);
                                  }}
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
